import { useEffect } from "react";
import { useManagedAdNetworks } from "../../../../common/hooks/dataRecipientSettingsHooks";
import { parseCheckboxSurveyAnswer } from "../../../util/surveyAnswerParsing";
import { Api } from "../../../../common/service/Api";

const useClearSelections = (
  orgId: string,
  answers: any,
  setAnswer: (slug: string, answer: string) => void,
  predeterminedChanges?: any,
) => {
  useEffect(() => {
    const changes = predeterminedChanges || {};
    if (
      answers["share-data-for-advertising"] === "false" &&
      answers["vendor-sharing-categories"] !== "[]"
    ) {
      changes["vendor-sharing-categories"] = "[]";
    }
    if (
      answers["disclosure-for-selling"] === "false" &&
      answers["vendor-selling-selection"] !== "[]"
    ) {
      changes["vendor-selling-selection"] = "[]";
    }
    if (answers["disclosure-for-selling"] === "false" && answers["selling-upload-data"] !== "") {
      changes["selling-upload-data"] = "";
    }
    if (
      answers["disclosure-for-selling"] === "false" &&
      answers["vendor-upload-data-selection"] !== "[]"
    ) {
      changes["vendor-upload-data-selection"] = "[]";
    }
    if (
      answers["selling-upload-data"] === "false" &&
      answers["vendor-upload-data-selection"] !== "[]"
    ) {
      changes["vendor-upload-data-selection"] = "[]";
    }
    if (Object.keys(changes).length > 0) {
      for (const slug of Object.keys(changes)) {
        const answer = changes[slug];
        setAnswer(slug, answer);
      }
    }
  }, [orgId, answers, setAnswer]);
};

const useManagedAdNetworkEffects = (orgId: string, answers: Record<string, string>) => {
  const { managedAdNetworks, refresh } = useManagedAdNetworks(orgId);

  useEffect(() => {
    // Process all UI-only questions for managed ad networks
    const uiOnlyAnswers = Object.keys(answers || {}).filter((slug) =>
      slug.startsWith("__ui_only__custom-audience-managed-by-"),
    );

    // Don't process if we don't have the necessary data
    if (!managedAdNetworks || uiOnlyAnswers.length === 0) {
      return;
    }

    const processNetworkChanges = async () => {
      for (const slug of uiOnlyAnswers) {
        const managerId = slug.replace("__ui_only__custom-audience-managed-by-", "");
        const selectedNetworkIds = parseCheckboxSurveyAnswer(answers[slug]);

        // Skip if no valid selection
        if (
          selectedNetworkIds.length === 0 ||
          (selectedNetworkIds.length === 1 && selectedNetworkIds[0] === "none")
        ) {
          continue;
        }

        // Get existing networks for this manager - ensure string comparison
        const existingForManager = managedAdNetworks.filter(
          (network) => network.managerId.toString() === managerId,
        );
        const existingNetworkIds = existingForManager.map((network) =>
          network.networkId.toString(),
        );

        // Find networks to add and remove
        const networksToAdd = selectedNetworkIds.filter(
          (networkId) =>
            !existingNetworkIds.includes(networkId) &&
            networkId !== "selectAll" &&
            networkId !== "none",
        );
        const networksToRemove = existingNetworkIds.filter(
          (networkId) => !selectedNetworkIds.includes(networkId),
        );

        // Add new relationships using the working API pattern
        for (let i = 0; i < networksToAdd.length; i++) {
          const networkId = networksToAdd[i];
          try {
            await Api.managedAdNetworks.createManagedAdNetwork(orgId, {
              managerId,
              networkId,
            });
            // Add a small delay between requests to prevent rate limiting
            if (i < networksToAdd.length - 1) {
              await new Promise((resolve) => setTimeout(resolve, 100));
            }
          } catch (error) {
            console.error(
              `Failed to create managed ad network relationship for manager ${managerId} and network ${networkId}:`,
              error,
            );
          }
        }

        // Remove old relationships using the working API pattern
        for (let i = 0; i < networksToRemove.length; i++) {
          const networkId = networksToRemove[i];
          try {
            await Api.managedAdNetworks.deleteManagedAdNetworkByIds(
              orgId,
              parseInt(managerId),
              parseInt(networkId),
            );
            // Add a small delay between requests to prevent rate limiting
            if (i < networksToRemove.length - 1) {
              await new Promise((resolve) => setTimeout(resolve, 100));
            }
          } catch (error) {
            console.error(
              `Failed to delete managed ad network relationship for manager ${managerId} and network ${networkId}:`,
              error,
            );
          }
        }
      }

      // Refresh the data after all operations like the working pattern does
      refresh();
    };

    processNetworkChanges();
  }, [orgId, JSON.stringify(answers), managedAdNetworks, refresh]);
};

// Form side-effect responses. This is done here to be in keeping with the general design of the form to have
// these kinds of "automatic classification" actions happen on the front-end. In general, this overall design
// should probably be moved to the back-end, and have the service be responsible for handling these kinds of
// automatic actions based on individual questions.
export const useSellingAndSharingAnswerEffects = (
  orgId: string,
  answers: Record<string, string>,
  setAnswer: (slug: string, answer: string, question?: string) => void,
  changes?: Record<string, string>,
): void => {
  useClearSelections(orgId, answers, setAnswer, changes);
  useManagedAdNetworkEffects(orgId, answers);
};
